import {Box, Button, Container, Flex, List, Text, VStack} from '@chakra-ui/react';
import {colors} from 'components/providers';
import {Counter, Price} from 'components/shared';
import {useTranslation} from 'hooks';
import {FC} from 'react';
import {ICartItem} from 'types';
import ImageUI from 'ui/ImageUI';
import {calculateProductOptionsPrice} from 'utils/cartUtils';

import {OptionItem} from './components';

interface Props {
  data: ICartItem;
  countable?: boolean;
  onQuantityChange?: (value: number, meta: {cartItemId: string}) => void;
  onItemClick?: () => void;
  onItemDelete?: (cartItemId: string) => void;
  currency: string;
  options?: {
    name: string;
    option: number;
    price: number;
    total_price: number;
    quantity: number;
  }[];
  ingredients?: string;
}

export const OrderProduct: FC<Props> = ({
  data,
  countable = false,
  onQuantityChange,
  onItemClick,
  onItemDelete,
  currency,
  options = [],
  ingredients,
}) => {
  const {t} = useTranslation();
  const {id, product, quantity, selectedOptions = {}} = data;
  const productIngredients = ingredients ? ingredients : product.ingredients;

  const calcTotalPrice = () => {
    const {optionsPrice, discountOptions} = calculateProductOptionsPrice(data);

    if (discountOptions) return discountOptions; //(optionsPrice + product.discount_price) * quantity;
    return (optionsPrice + product.discount_price) * quantity;
  };

  const calcItemPrice = () => {
    let optionsPrice = 0;
    options?.map((option) => {
      optionsPrice += option.total_price;
    });

    return optionsPrice + (product.discount_price || product.price);
  };

  const itemPrice = calcItemPrice();

  const handleQuantityChange = (newValue: number) => {
    if (onQuantityChange) {
      onQuantityChange(newValue, {
        cartItemId: id,
      });
    }
  };

  return (
    <Container variant="white-block" w="100%" position="relative">
      <Flex w="100%">
        <Box
          minW={['70px', '120px']}
          w={['70px', '120px']}
          h={['60px', '100px']}
          onClick={onItemClick}
          cursor={countable ? 'pointer' : 'default'}
        >
          <ImageUI
            w="100%"
            h="100%"
            mr="30px"
            borderRadius="1rem"
            objectFit="cover"
            // because of casting data in cart
            src={product.product_img_for_list_thumbnail || product.image || '/food_pattern.webp'}
            alt={`${product.name} image`}
          />
        </Box>
        <VStack
          color={colors.lightBlack}
          w="100%"
          ml="30px"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing="10px"
        >
          <Text
            fontWeight="600"
            fontSize={['14px', '16px']}
            mr="30px"
            onClick={onItemClick}
            cursor={countable ? 'pointer' : 'default'}
          >
            {product.name}
          </Text>
          {productIngredients && (
            <Text as="span" fontSize={['12px', '14px']} fontWeight="500" width="90%" color={colors.textGray}>
              {productIngredients}
            </Text>
          )}
          <List>
            {/* FOR CART ! */}
            {Object.keys(selectedOptions).map((setId) =>
              Object.values(selectedOptions[parseInt(setId)]).map((option_set) => {
                return (
                  <OptionItem
                    quantity={option_set.quantity}
                    optionName={option_set.name}
                    optionPrice={option_set.price}
                    currency={currency}
                    key={option_set.option}
                  />
                );
              }),
            )}
            {/* FOR ORDER PREVIEW */}
            {options?.map((option_set) => {
              return (
                <OptionItem
                  quantity={option_set.quantity}
                  optionName={option_set.name}
                  optionPrice={option_set.price}
                  currency={currency}
                  key={option_set.option}
                />
              );
            })}
          </List>
          {data?.instructions && (
            <Text
              as="span"
              fontSize={['14px', '16px']}
              fontWeight="500"
              width="90%"
              pb="5px"
              mb="auto !important"
              color={colors.textGray}
              fontStyle="italic"
            >
              “{data?.instructions}”
            </Text>
          )}
          {countable && onItemDelete && (
            <Button
              position="absolute"
              minW="30px"
              h="30px"
              borderRadius="50%"
              bg={colors.lightGray}
              p={0}
              right="15px"
              top="0px"
              onClick={() => onItemDelete(id)}
            >
              <ImageUI src="/delete-icon.svg" alt="delete" />
            </Button>
          )}
          <Flex w="100%" justifyContent="space-between" alignItems="center" marginTop="auto">
            {countable && onQuantityChange && (
              <Counter onChange={handleQuantityChange} defaultValue={quantity} min={0} />
            )}
            <Text fontWeight="600" fontSize="1rem" ml="auto">
              {!countable ? (
                <>
                  {data?.product?.is_weighted_product ? (
                    <>
                      {quantity.toFixed(1)} x <Price value={data.price} /> ({product.price} {t('CURRENCY')} /{' '}
                      {data?.pricing_unit})
                    </>
                  ) : (
                    <>
                      {quantity} x <Price value={itemPrice} />
                    </>
                  )}
                </>
              ) : (
                <Price value={`${calcTotalPrice()}`} priceProps={{textAlign: 'end'}} />
              )}
            </Text>
          </Flex>
        </VStack>
      </Flex>
    </Container>
  );
};
